import { useCallback, useEffect, useRef, useState } from "react";
import SpotifyWebApi from "spotify-web-api-js";
import { useTokenContext } from "src/auth/UserTokenProvider";

const spotifyApi = new SpotifyWebApi();
const SPOTIFY_BATCH_LIMIT = 50;
const ARTIST_RELEASES_PER_LOAD = 20;
const SPOTIFY_MAX_RETRIES = 4;
const SPOTIFY_DEFAULT_RETRY_AFTER_MS = 2500;

export interface RankedArtistTrack {
  id: string;
  uri: string;
  name: string;
  popularity: number;
  duration_ms: number;
  artists: SpotifyApi.ArtistObjectSimplified[];
  album: SpotifyApi.AlbumObjectSimplified;
  liked: boolean;
}

const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const normalizeValue = (value: string): string => value.trim().toLowerCase().replace(/\s+/g, " ");

const getDuplicateKey = (track: SpotifyApi.TrackObjectFull): string => {
  const artists = track.artists
    .map(artist => normalizeValue(artist.name))
    .sort()
    .join("|");
  return `${normalizeValue(track.name)}::${artists}`;
};

const delay = (milliseconds: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, milliseconds));

const getRetryAfterMs = (err: unknown): number => {
  const request = err as { getResponseHeader?: (header: string) => string | null };
  const retryAfter = request.getResponseHeader?.("Retry-After");
  const retryAfterSeconds = retryAfter ? Number(retryAfter) : NaN;

  if (!isNaN(retryAfterSeconds) && retryAfterSeconds >= 0) {
    return retryAfterSeconds * 1000;
  }

  return SPOTIFY_DEFAULT_RETRY_AFTER_MS;
};

const isRateLimited = (err: unknown): boolean => {
  return typeof err === "object" && err !== null && (err as { status?: number }).status === 429;
};

const spotifyRequestWithRetry = async <T>(request: () => Promise<T>): Promise<T> => {
  let attempt = 0;

  while (true) {
    try {
      return await request();
    } catch (err) {
      if (!isRateLimited(err) || attempt >= SPOTIFY_MAX_RETRIES) {
        throw err;
      }

      await delay(getRetryAfterMs(err) + attempt * 1000);
      attempt += 1;
    }
  }
};

const fetchArtistAlbumsPage = async (
  artistId: string,
  offset: number
): Promise<SpotifyApi.ArtistsAlbumsResponse> =>
  spotifyRequestWithRetry(() =>
    spotifyApi.getArtistAlbums(artistId, {
      include_groups: "album,single",
      limit: ARTIST_RELEASES_PER_LOAD,
      offset
    })
  );

const fetchAllAlbumTrackIds = async (albumId: string): Promise<string[]> => {
  const trackIds: string[] = [];
  let offset = 0;
  let total = 0;

  do {
    const currentOffset = offset;
    const response = await spotifyRequestWithRetry(() =>
      spotifyApi.getAlbumTracks(albumId, {
        limit: SPOTIFY_BATCH_LIMIT,
        offset: currentOffset
      })
    );
    trackIds.push(...response.items.map(track => track.id));
    total = response.total;
    offset += response.items.length;
  } while (offset < total);

  return trackIds;
};

const fetchFullTracks = async (trackIds: string[]): Promise<SpotifyApi.TrackObjectFull[]> => {
  const tracks: SpotifyApi.TrackObjectFull[] = [];

  for (const ids of chunk(trackIds, SPOTIFY_BATCH_LIMIT)) {
    const response = await spotifyRequestWithRetry(() => spotifyApi.getTracks(ids));
    tracks.push(...response.tracks);
  }

  return tracks;
};

const fetchLikedTrackIds = async (trackIds: string[]): Promise<Set<string>> => {
  const likedTrackIds = new Set<string>();

  for (const ids of chunk(trackIds, SPOTIFY_BATCH_LIMIT)) {
    const containsSavedTracks = await spotifyRequestWithRetry(() =>
      spotifyApi.containsMySavedTracks(ids)
    );
    ids.forEach((id, index) => {
      if (containsSavedTracks[index]) {
        likedTrackIds.add(id);
      }
    });
  }

  return likedTrackIds;
};

const rankAndDeduplicateTracks = (tracks: SpotifyApi.TrackObjectFull[]): RankedArtistTrack[] => {
  const tracksByDuplicateKey = new Map<string, SpotifyApi.TrackObjectFull>();

  tracks.forEach(track => {
    const duplicateKey = getDuplicateKey(track);
    const existingTrack = tracksByDuplicateKey.get(duplicateKey);

    if (!existingTrack || track.popularity > existingTrack.popularity) {
      tracksByDuplicateKey.set(duplicateKey, track);
    }
  });

  return Array.from(tracksByDuplicateKey.values())
    .sort((a, b) => b.popularity - a.popularity)
    .map(track => ({
      id: track.id,
      uri: track.uri,
      name: track.name,
      popularity: track.popularity,
      duration_ms: track.duration_ms,
      artists: track.artists,
      album: track.album,
      liked: false
    }));
};

const applyLikedTrackIds = (
  tracks: RankedArtistTrack[],
  likedTrackIds: Set<string>
): RankedArtistTrack[] =>
  tracks.map(track => ({
    ...track,
    liked: likedTrackIds.has(track.id)
  }));

const mergeTracksByPopularity = (
  currentTracks: Map<string, SpotifyApi.TrackObjectFull>,
  newTracks: SpotifyApi.TrackObjectFull[]
) => {
  newTracks.forEach(track => {
    const duplicateKey = getDuplicateKey(track);
    const existingTrack = currentTracks.get(duplicateKey);

    if (!existingTrack || track.popularity > existingTrack.popularity) {
      currentTracks.set(duplicateKey, track);
    }
  });
};

const useArtistPopularTracks = (artistId?: string) => {
  const { token, isLoading: tokenLoading } = useTokenContext();
  const [artist, setArtist] = useState<SpotifyApi.SingleArtistResponse | null>(null);
  const [tracks, setTracks] = useState<RankedArtistTrack[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const albumOffsetRef = useRef(0);
  const albumTotalRef = useRef<number | null>(null);
  const seenAlbumIdsRef = useRef<Set<string>>(new Set());
  const tracksByDuplicateKeyRef = useRef<Map<string, SpotifyApi.TrackObjectFull>>(new Map());
  const likedTrackIdsRef = useRef<Set<string>>(new Set());

  const resetCatalog = useCallback(() => {
    albumOffsetRef.current = 0;
    albumTotalRef.current = null;
    seenAlbumIdsRef.current = new Set();
    tracksByDuplicateKeyRef.current = new Map();
    likedTrackIdsRef.current = new Set();
    setArtist(null);
    setTracks([]);
    setHasMore(false);
    setError(null);
  }, []);

  const loadMore = useCallback(async (): Promise<number> => {
    if (!artistId || !token?.value) {
      return tracksByDuplicateKeyRef.current.size;
    }

    const total = albumTotalRef.current;
    if (total !== null && albumOffsetRef.current >= total) {
      setHasMore(false);
      return tracksByDuplicateKeyRef.current.size;
    }

    setLoadingMore(true);
    try {
      spotifyApi.setAccessToken(token.value);
      const albumsResponse = await fetchArtistAlbumsPage(artistId, albumOffsetRef.current);
      albumTotalRef.current = albumsResponse.total;
      albumOffsetRef.current += albumsResponse.items.length;

      const albums = albumsResponse.items.filter(album => {
        if (seenAlbumIdsRef.current.has(album.id)) {
          return false;
        }
        seenAlbumIdsRef.current.add(album.id);
        return true;
      });

      const trackIdsByAlbum: string[][] = [];
      for (const album of albums) {
        trackIdsByAlbum.push(await fetchAllAlbumTrackIds(album.id));
      }

      const uniqueTrackIds = Array.from(new Set(trackIdsByAlbum.flat()));
      const fullTracks = await fetchFullTracks(uniqueTrackIds);
      const batchLikedTrackIds = await fetchLikedTrackIds(fullTracks.map(track => track.id));
      batchLikedTrackIds.forEach(trackId => likedTrackIdsRef.current.add(trackId));

      mergeTracksByPopularity(tracksByDuplicateKeyRef.current, fullTracks);
      const rankedTracks = applyLikedTrackIds(
        rankAndDeduplicateTracks(Array.from(tracksByDuplicateKeyRef.current.values())),
        likedTrackIdsRef.current
      );

      setTracks(rankedTracks);
      setHasMore(albumOffsetRef.current < albumsResponse.total);
      return rankedTracks.length;
    } catch (err) {
      setError(
        isRateLimited(err)
          ? "Spotify rate limit reached while loading artist tracks. Please try again in a minute."
          : "Failed to fetch artist tracks"
      );
      return tracksByDuplicateKeyRef.current.size;
    } finally {
      setLoadingMore(false);
    }
  }, [artistId, token?.value]);

  useEffect(() => {
    let isCurrentRequest = true;
    resetCatalog();
    const fetchArtistCatalog = async () => {
      if (!artistId) {
        return;
      }
      if (!token?.value) {
        setLoading(tokenLoading);
        setError(tokenLoading ? null : "Spotify authentication is required");
        return;
      }

      try {
        spotifyApi.setAccessToken(token.value);
        setLoading(true);
        setError(null);

        const artistResponse = await spotifyRequestWithRetry(() => spotifyApi.getArtist(artistId));

        if (isCurrentRequest) {
          setArtist(artistResponse);
        }

        if (isCurrentRequest) {
          await loadMore();
        }
      } catch (err) {
        if (isCurrentRequest) {
          setError(
            isRateLimited(err)
              ? "Spotify rate limit reached while loading artist tracks. Please try again in a minute."
              : "Failed to fetch artist tracks"
          );
        }
      } finally {
        if (isCurrentRequest) {
          setLoading(false);
        }
      }
    };

    fetchArtistCatalog();

    return () => {
      isCurrentRequest = false;
    };
  }, [artistId, loadMore, resetCatalog, token?.value, tokenLoading]);

  return { artist, tracks, loading, loadingMore, hasMore, loadMore, error };
};

export default useArtistPopularTracks;
